import { EventActorSummary } from "@/types/event-actor"
import { PlaceSummary } from "@/types/place"
import { WebSearchResultEvent } from "@/types/web-search-result"

import { baseApi } from "./base-api"

/** The server caps a request at 100 refs. */
const MAX_REFS_PER_REQUEST = 100

interface GetWebEventsResponse {
  web_events: WebSearchResultEvent[]
  places: PlaceSummary[]
  event_actors: EventActorSummary[]
}

/** Events plus the venues (by `venue.ref`) and actors (by performer `ref`) they link to. */
export interface WebEventsLookup {
  events: WebSearchResultEvent[]
  places: PlaceSummary[]
  eventActors: EventActorSummary[]
}

export interface GetWebEventsArgs {
  /** Web event refs, e.g. ["we-7aLZJ9Hw"]. */
  refs: string[]
}

/**
 * Open-web events, looked up by ref.
 *
 * Chat receives its events inline with the message that produced them. Anything downstream of
 * that message — an agent run report citing `we-…`, the internal card gallery — holds refs
 * only, and needs them turned back into renderable events. Refs that no longer resolve (the
 * sweep drops past-date events nobody referenced) come back absent rather than as an error,
 * so callers match on `ref` rather than by position.
 */
export const webEventsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getWebEvents: builder.query<WebEventsLookup, GetWebEventsArgs>({
      queryFn: async ({ refs }, _queryApi, _extraOptions, baseQuery) => {
        const unique = [...new Set(refs)]
        const batches: string[][] = []
        for (let i = 0; i < unique.length; i += MAX_REFS_PER_REQUEST) {
          batches.push(unique.slice(i, i + MAX_REFS_PER_REQUEST))
        }

        const lookup: WebEventsLookup = {
          events: [],
          places: [],
          eventActors: [],
        }
        for (const batch of batches) {
          const result = await baseQuery({
            url: `/api/web_events?refs=${encodeURIComponent(batch.join(","))}`,
          })
          if (result.error) return { error: result.error }
          const data = result.data as GetWebEventsResponse
          lookup.events.push(...(data.web_events ?? []))
          lookup.places.push(...(data.places ?? []))
          lookup.eventActors.push(...(data.event_actors ?? []))
        }
        return { data: lookup }
      },
      providesTags: (result) =>
        result
          ? result.events.map((event) => ({ type: "WebEvents", id: event.ref }))
          : [],
    }),
  }),
})

export const { useGetWebEventsQuery } = webEventsApi
